import React, { Component, type ReactNode } from 'react';

import { Button, Result } from 'antd';
import { Trans } from 'react-i18next';
import { translate } from '../../../utils/translation';

/**
 * The state interface for ErrorBoundary.
 * - hasError: Whether an error has been caught.
 * - errorInfo: The info about the error, if any.
 * - secondsLeft: Seconds before automatic reload.
 */
interface State {
  hasError: boolean;
  errorInfo: React.ErrorInfo | null;
  secondsLeft: number;
}

/**
 * Props for ErrorBoundary.
 * - children: Components to render within the boundary.
 */
interface Props {
  children: ReactNode;
}

/**
 * ErrorBoundary component.
 *
 * This React class component catches errors in its children,
 * displays a fallback UI, and automatically reloads the page after a short countdown.
 */
class ErrorBoundary extends Component<Props, State> {
  // Timeout ID for scheduling page reload
  private _reloadTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // Interval ID for countdown tick
  private _tickIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Clears any active timers (interval and timeout) to prevent memory leaks or unwanted reloads.
   */
  private clearTimers() {
    if (this._reloadTimeoutId && typeof clearTimeout === 'function') {
      try {
        clearTimeout(this._reloadTimeoutId);
      } catch (error) {
        // Ignore errors when clearing timers (e.g., in test environments)
        console.warn('Failed to clear timeout:', error);
      }
      this._reloadTimeoutId = null;
    }
    if (this._tickIntervalId && typeof clearInterval === 'function') {
      try {
        clearInterval(this._tickIntervalId);
      } catch (error) {
        // Ignore errors when clearing timers (e.g., in test environments)
        console.warn('Failed to clear interval:', error);
      }
      this._tickIntervalId = null;
    }
  }

  /**
   * Starts an automatic page reload sequence.
   * - Shows a countdown.
   * - Reloads the page after the specified number of milliseconds.
   * @param totalMs Total milliseconds before reload.
   */
  private startAutoReload(totalMs = 5000) {
    // Reset any previous timers to avoid overlap
    this.clearTimers();

    // Calculate the number of countdown seconds based on totalMs (default: 5)
    const totalSeconds = Math.max(1, Math.round(totalMs / 1000));
    this.setState({ secondsLeft: totalSeconds });

    // Every second, decrement the countdown timer in state
    this._tickIntervalId = setInterval(() => {
      this.setState((prev) => ({ secondsLeft: Math.max(0, prev.secondsLeft - 1) }));
    }, 1000);

    // After totalMs, reload the entire page
    this._reloadTimeoutId = setTimeout(() => {
      window.location.reload();
    }, totalMs);
  }

  /**
   * The component's initial state: no error, countdown set to 5 seconds.
   */
  public state: State = {
    errorInfo: null,
    hasError: false,
    secondsLeft: 5,
  };

  /**
   * Standard constructor.
   * @param props Component props.
   */
  constructor(props: Props) {
    super(props);
    this.state = { errorInfo: null, hasError: false, secondsLeft: 5 };
  }

  /**
   * React lifecycle static method: updates state when an error is thrown.
   * @param error The error thrown in a child component.
   * @returns Updated state representing error.
   */
  static getDerivedStateFromError(error: Error): State {
    // Log the error for debugging/monitoring
    console.error(error);
    // Only update hasError; errorInfo is set in componentDidCatch
    return { errorInfo: null, hasError: true, secondsLeft: 5 };
  }

  /**
   * React lifecycle method called after an error is thrown in a child.
   * Stores error info and starts the auto-reload countdown.
   * @param _ Error object (not used directly)
   * @param errorInfo React's error info for debugging
   */
  componentDidCatch(_: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo }, () => {
      this.startAutoReload(5000);
    });
  }

  /**
   * React lifecycle method: runs when the component will unmount.
   * Clears any running timers.
   */
  componentWillUnmount(): void {
    this.clearTimers();
  }

  /**
   * Renders the error fallback UI or children.
   * - If there is an error, display an Ant Design Result with a reload button and countdown.
   * - Otherwise, render children normally.
   */
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-white text-center">
          <Result
            status="500"
            title="Oops!"
            subTitle={
              <div>
                {/* Main error message */}
                <p>{translate('errors.hitASnag')}</p>
                {/* Countdown status */}
                <p className="text-gray-dark mt-2 text-sm">
                  <Trans>
                    {translate('common.reloadingPageIn', { count: this.state.secondsLeft })}
                  </Trans>
                </p>
              </div>
            }
            extra={
              <Button type="primary" onClick={() => window.location.reload()}>
                {translate('common.reloadPage')}
              </Button>
            }
          />
        </div>
      );
    }

    // No error: render children as normal
    return this.props.children;
  }
}

export default ErrorBoundary;
